import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { TenantApi } from "@/openapi/generated";
import type { GetPayJunctionResponse, UpdatePayJunctionRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface UsePayJunctionOptionsUpdateParams {
  tenantId: number;
  updatePayJunctionRequest: UpdatePayJunctionRequest;
}

export const usePayJunctionOptionsUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, isError } = useMutation({
    mutationFn: async ({
      tenantId,
      updatePayJunctionRequest,
    }: UsePayJunctionOptionsUpdateParams) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(TenantApi, accessToken);

      await service.apiAccountTenantIdPayjunctionOptionsPut({
        tenantId,
        updatePayJunctionRequest,
        xTenantID: tenantId,
      });

      return { tenantId, updatePayJunctionRequest };
    },
    onSuccess: (result) => {
      queryClient.setQueryData<GetPayJunctionResponse | null>(
        [QueryKeys.PayJunctionOptions, result.tenantId],
        (oldData) => ({
          userName: result.updatePayJunctionRequest.userName,
          enabled: result.updatePayJunctionRequest.enabled,
          publishableKey: oldData?.publishableKey ?? null,
        }),
      );

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PayJunctionOptions, result.tenantId],
      });

      toast({
        title: "PayJunction updated",
        description: "PayJunction credentials saved successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update PayJunction credentials";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, mutateAsync, isPending, isError, isSuccess };
};
